'use strict';

/**
 * CartDrawerManager - Gerencia o drawer do carrinho lateral.
 *
 * Usa event delegation para manter todos os eventos funcionais após refresh,
 * sem necessidade de rebind manual.
 *
 * Integra-se com:
 *   - window.CartManager  (cart-manager.js)   — operações de carrinho
 *   - window.headerEvents (header-manager.js)  — barramento de eventos global
 *   - Splide               (splide.min.js)     — slider de sugestões
 */
class CartDrawerManager {
  constructor(config = {}) {
    this.config = {
      sectionId: config.sectionId || '',
      cartUrl: config.cartUrl || '/cart',
      searchUrl: config.searchUrl || '/search',
    };

    this.drawer = document.querySelector('[data-cart-drawer]');
    this.overlay = document.querySelector('[data-cart-drawer-overlay]');
    this.isOpen = false;
    this._splideInstance = null;
    this._isRefreshing = false;
    this._historyStatePushed = false;
    this.activeView = 'cart';
    this._favoritesRequestToken = 0;
    this._favoritesInjectedStyleCache = new Set();

    // Bind do handler do popstate para poder remover depois
    this._boundPopStateHandler = this._handlePopState.bind(this);

    if (!this.drawer) {
      console.warn('[CartDrawerManager] Drawer element not found');
      return;
    }

    this._bindEvents();
    this._bindCartEvents();
    this._initSlider();
    this._switchView(this.activeView, { shouldLoadFavorites: false });
  }

  // ─── API Pública ────────────────────────────────────────────────────────────

  open() {
    if (this.isOpen) return;

    this.isOpen = true;
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    this.overlay?.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    if (window.headerEvents) window.headerEvents.emit('cartDrawer:opened');

    // Adiciona estado ao history para capturar o botão voltar (mobile)
    if (!this._historyStatePushed) {
      history.pushState({ cartDrawerOpen: true }, '', window.location.href);
      this._historyStatePushed = true;
    }
  }

  close() {
    if (!this.isOpen) return;

    // Se adicionamos um state ao history, usamos back() para fechar.
    // Isso dispara popstate → _handlePopState → _closeDrawer.
    if (this._historyStatePushed) {
      history.back();
      return;
    }

    this._closeDrawer();
  }

  /**
   * Fecha o drawer sem interagir com o history.
   * Chamado pelo _handlePopState após o back() já ter sido executado.
   */
  _closeDrawer() {
    this.isOpen = false;
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    this.overlay?.classList.remove('is-open');
    document.body.style.overflow = '';

    if (window.headerEvents) window.headerEvents.emit('cartDrawer:closed');

    // Notifica outros componentes (ex: cart-share) que o drawer fechou
    document.dispatchEvent(new CustomEvent('cartDrawer:closed'));
  }

  /**
   * Handler para o evento popstate (botão voltar do navegador).
   * Fecha o drawer em vez de navegar para a página anterior.
   */
  _handlePopState() {
    if (this.isOpen && this._historyStatePushed) {
      // Limpa a flag antes de fechar para evitar recursão
      this._historyStatePushed = false;
      this._closeDrawer();
    }
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  destroy() {
    this._splideInstance?.destroy();
    this._splideInstance = null;

    // Remove listener do popstate
    window.removeEventListener('popstate', this._boundPopStateHandler);
  }

  /**
   * Atualiza o conteúdo do drawer via Section Rendering API.
   *
   * Substitui todo o innerHTML do <aside> de uma vez, garantindo que
   * elementos condicionais (footer, free-shipping bar) apareçam e
   * desapareçam corretamente conforme o estado do carrinho.
   *
   * Previne chamadas concorrentes com a flag _isRefreshing.
   */
  async refresh() {
    if (this._isRefreshing) return;
    this._isRefreshing = true;

    try {
      const response = await fetch(
        `${this.config.cartUrl}?section_id=${this.config.sectionId}`
      );
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      // Substitui todo o conteúdo do drawer de uma vez.
      // Isso garante que header, content, free-shipping bar e footer
      // (todos condicionais) sejam inseridos/removidos corretamente.
      const newDrawer = doc.querySelector('[data-cart-drawer]');
      if (newDrawer) {
        this.drawer.innerHTML = newDrawer.innerHTML;
      }

      // Re-init Share Cart se disponível
      if (typeof window.initShareCart === 'function') window.initShareCart();

      // Re-init slider (o elemento foi substituído acima)
      this._initSlider();
      this._switchView(this.activeView, { shouldLoadFavorites: this.activeView === 'favorites' });
    } catch (error) {
      console.error('[CartDrawerManager] Refresh error:', error);
    } finally {
      this._isRefreshing = false;
    }
  }

  // ─── Privado ───────────────────────────────────────────────────────────────

  /**
   * Inicializa o Splide no slider de sugestões.
   * Aguarda o Splide estar disponível se ainda não carregou.
   */
  _initSlider() {
    if (typeof Splide === 'undefined') {
      setTimeout(() => this._initSlider(), 50);
      return;
    }

    const slider = this.drawer.querySelector('[data-suggestions-slider]');
    if (!slider) return;

    // Destrói instância anterior para evitar conflitos
    if (slider.splide) {
      slider.splide.destroy();
      slider.splide = null;
    }

    this._splideInstance = new Splide(slider, {
      type: 'slide',
      perPage: 2,
      perMove: 1,
      gap: '1.6rem',
      padding: '1.6rem',
      arrows: false,
      pagination: false,
      drag: true,
      breakpoints: {
        350: {
          fixedWidth: '90%',
          perPage: 1,
        },
      },
    });

    this._splideInstance.mount();
    slider.splide = this._splideInstance;

    // Carrega cores para cards Liquid pré-renderizados
    slider.querySelectorAll('.productCard').forEach(card => {
      this._loadCardColorsFromDOM(card);
    });
  }

  /**
   * Carrega swatches de cor para todos os containers de cor de um card.
   * Espelha a lógica do SearchDrawerManager._loadCardColorsFromDOM.
   */
  _loadCardColorsFromDOM(card) {
    const mainContainer = card.querySelector('[data-main-colors]');
    const relatedContainer = card.querySelector('[data-related-colors]');

    if (mainContainer) this._loadColorSiblings(mainContainer);
    if (relatedContainer) this._loadColorSiblings(relatedContainer);
  }

  /**
   * Busca produtos irmãos via Predictive Search API e renderiza swatches.
   */
  async _loadColorSiblings(container) {
    const variantId = container.dataset.variantId;
    const currentProductId = parseInt(container.dataset.productId);

    if (!variantId) {
      this._removeColorSkeletons(container);
      return;
    }

    try {
      const response = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(variantId)}&resources[type]=product&resources[limit]=25`
      );
      const data = await response.json();
      const products = data.resources?.results?.products || [];
      const siblings = products.filter(p => p.id !== currentProductId);

      if (siblings.length === 0) {
        this._removeColorSkeletons(container);
        return;
      }

      let pending = siblings.length;
      const onComplete = () => {
        pending--;
        if (pending <= 0) this._removeColorSkeletons(container);
      };

      siblings.forEach(prod => this._fetchSiblingColorData(prod, variantId, container, onComplete));
    } catch (err) {
      console.error('[CartDrawerManager:Color] Erro na Search API:', err);
      this._removeColorSkeletons(container);
    }
  }

  /**
   * Busca dados de cor de um produto via Section Rendering API e renderiza swatch.
   */
  async _fetchSiblingColorData(prod, variantId, container, onComplete) {
    try {
      const response = await fetch(`/products/${prod.handle}?sections=pdp-main`);
      const sections = await response.json();
      const html = sections['pdp-main'];
      if (!html) { onComplete(); return; }

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const script = doc.querySelector('[data-pdp-color-data]');
      if (!script) { onComplete(); return; }

      const colorData = JSON.parse(script.textContent);
      if (colorData.variantId !== variantId) { onComplete(); return; }

      const swatch = document.createElement('a');
      swatch.href = colorData.productUrl;
      swatch.className = 'productCard__colorSwatch block w-[0.6rem] h-[0.6rem] transition-colors';
      swatch.title = colorData.productTitle;
      swatch.style.opacity = '0';
      swatch.style.transition = 'opacity 0.3s ease';

      if (colorData.pattern) {
        swatch.innerHTML = `<img src="${colorData.pattern}" alt="${colorData.productTitle}" class="w-full h-full object-cover" width="16" height="16" />`;
      } else if (colorData.color) {
        swatch.innerHTML = `<span class="block w-full h-full" style="background-color: ${colorData.color};"></span>`;
      }

      const firstSkeleton = container.querySelector('[data-color-skeleton]');
      if (firstSkeleton) {
        container.insertBefore(swatch, firstSkeleton);
      } else {
        container.appendChild(swatch);
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => { swatch.style.opacity = '1'; });
      });

      onComplete();
    } catch (err) {
      console.error('[CartDrawerManager:Color] Erro ao buscar cor:', err);
      onComplete();
    }
  }

  /**
   * Remove os skeletons de um container de cores.
   */
  _removeColorSkeletons(container) {
    container.querySelectorAll('[data-color-skeleton]').forEach(el => {
      el.style.transition = 'opacity 0.2s ease';
      el.style.opacity = '0';
      setTimeout(() => el.remove(), 200);
    });
  }

  _ensureFetchedStyles(doc) {
    const styles = doc.querySelectorAll('style');

    styles.forEach(style => {
      const css = style.textContent ? style.textContent.trim() : '';
      if (!css || this._favoritesInjectedStyleCache.has(css)) return;

      const alreadyExists = Array.from(document.querySelectorAll('style')).some(existingStyle => {
        return existingStyle.textContent && existingStyle.textContent.trim() === css;
      });

      if (alreadyExists) {
        this._favoritesInjectedStyleCache.add(css);
        return;
      }

      const clonedStyle = document.createElement('style');
      clonedStyle.setAttribute('data-cart-drawer-favorites-style', '');
      clonedStyle.textContent = css;
      document.head.appendChild(clonedStyle);
      this._favoritesInjectedStyleCache.add(css);
    });
  }

  _switchView(view, options = {}) {
    const { shouldLoadFavorites = true } = options;
    const nextView = view === 'favorites' ? 'favorites' : 'cart';
    const cartView = this.drawer.querySelector('[data-cart-drawer-view="cart"]');
    const favoritesView = this.drawer.querySelector('[data-cart-drawer-view="favorites"]');

    this.activeView = nextView;

    if (cartView) cartView.classList.toggle('hidden', nextView !== 'cart');
    if (favoritesView) favoritesView.classList.toggle('hidden', nextView !== 'favorites');

    this.drawer.querySelectorAll('[data-cart-drawer-view-toggle]').forEach(toggle => {
      const isActive = toggle.dataset.cartDrawerViewToggle === nextView;
      toggle.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      toggle.classList.toggle('border-b', isActive);
      toggle.classList.toggle('border-black', isActive);
    });

    const freeShippingBar = this.drawer.querySelector('[data-free-shipping-bar]');
    if (freeShippingBar) freeShippingBar.classList.toggle('hidden', nextView !== 'cart');

    const footer = this.drawer.querySelector('[data-cart-footer]');
    if (footer) footer.classList.toggle('hidden', nextView !== 'cart');

    if (nextView === 'favorites' && shouldLoadFavorites) {
      this._renderFavorites();
    }
  }

  async _renderFavorites() {
    const loadingState = this.drawer.querySelector('[data-cart-favorites-loading]');
    const emptyState = this.drawer.querySelector('[data-cart-favorites-empty]');
    const grid = this.drawer.querySelector('[data-cart-favorites-grid]');

    if (!loadingState || !emptyState || !grid) return;

    const setState = (state, errorMessage = '') => {
      loadingState.classList.toggle('hidden', state !== 'loading');
      emptyState.classList.toggle('hidden', state !== 'empty');
      grid.classList.toggle('hidden', state !== 'grid');

      if (state === 'error') {
        loadingState.classList.remove('hidden');
        loadingState.innerHTML = `<p class="text-[1.2rem] uppercase tracking-wider text-center">${errorMessage || 'Ops, houve um erro ao carregar.'}</p>`;
      } else if (state === 'loading') {
        loadingState.innerHTML = `
          <div class="flex items-center gap-[1.2rem] text-[1.2rem] uppercase tracking-wider">
            <svg class="animate-spin h-[2.4rem] w-[2.4rem]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Loading favorites...</span>
          </div>
        `;
      }
    };

    setState('loading');

    if (!window.FavoritesManager) {
      setState('empty');
      return;
    }

    const ids = window.FavoritesManager.get();
    if (ids.length === 0) {
      grid.innerHTML = '';
      setState('empty');
      return;
    }

    const requestToken = ++this._favoritesRequestToken;
    const query = ids.map(id => `id:${id}`).join(' OR ');

    try {
      const response = await fetch(`${this.config.searchUrl}?q=${encodeURIComponent(query)}&type=product&section_id=${this.config.sectionId}`);
      const html = await response.text();

      if (requestToken !== this._favoritesRequestToken) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      this._ensureFetchedStyles(doc);

      const cards = doc.querySelectorAll('.product-card-rendered-from-search');
      if (cards.length === 0) {
        grid.innerHTML = '';
        setState('empty');
        return;
      }

      grid.innerHTML = '';
      cards.forEach(card => grid.appendChild(card));
      setState('grid');

      if (window.FavoritesManager) {
        window.FavoritesManager.updateUI();
      }

      grid.querySelectorAll('.productCard').forEach(card => this._loadCardColorsFromDOM(card));
    } catch (error) {
      console.error('[CartDrawerManager] Favorites render error:', error);
      setState('error');
    }
  }

  /**
   * Registra todos os event listeners usando delegação de eventos.
   * Isso garante que os handlers funcionem mesmo após refresh do DOM.
   */
  _bindEvents() {
    // Fechar ao clicar no botão de fechar (dentro do drawer)
    this.drawer.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-drawer-close]')) this.close();
    });

    // Fechar ao clicar no overlay
    this.overlay?.addEventListener('click', () => this.close());

    // Fechar via ESC
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });

    // Abrir via botões externos (header, page, etc.)
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-drawer-open]')) {
        e.preventDefault();
        this.open();
      }
    });

    // Escuta o botão voltar do navegador (mobile)
    window.addEventListener('popstate', this._boundPopStateHandler);

    // Delegação de cliques dentro do drawer para ações de itens
    this.drawer.addEventListener('click', (e) => this._handleClick(e));

    // Enter no input de cupom
    this.drawer.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && e.target.matches('[data-coupon-input]')) {
        e.preventDefault();
        this._applyCoupon();
      }
    });

    // Máscara de CEP
    this.drawer.addEventListener('input', (e) => {
      if (!e.target.matches('[data-shipping-input]')) return;
      let value = e.target.value.replace(/\D/g, '');
      if (value.length > 5) value = value.substring(0, 5) + '-' + value.substring(5, 8);
      e.target.value = value;
    });
  }

  /**
   * Dispatch handler centralizado para cliques dentro do drawer.
   * Usa closest() para suportar cliques em elementos filhos dos botões.
   */
  _handleClick(e) {
    const viewToggleBtn = e.target.closest('[data-cart-drawer-view-toggle]');
    if (viewToggleBtn) {
      e.preventDefault();
      e.stopPropagation();
      this._switchView(viewToggleBtn.dataset.cartDrawerViewToggle);
      return;
    }

    const removeBtn = e.target.closest('[data-remove-item]');
    if (removeBtn) {
      e.stopPropagation();
      const key = removeBtn.dataset.lineItemKey;
      if (key) this._removeItem(removeBtn, key);
      return;
    }

    const minusBtn = e.target.closest('[data-quantity-minus]');
    if (minusBtn) {
      e.stopPropagation();
      const key = minusBtn.dataset.lineItemKey;
      const qty = this._getItemQty(minusBtn);
      if (key && qty > 1) this._updateQty(minusBtn, key, qty - 1);
      return;
    }

    const plusBtn = e.target.closest('[data-quantity-plus]');
    if (plusBtn) {
      e.stopPropagation();
      const key = plusBtn.dataset.lineItemKey;
      const qty = this._getItemQty(plusBtn);
      if (key) this._updateQty(plusBtn, key, qty + 1);
      return;
    }

    const giftWrapAddBtn = e.target.closest('[data-gift-wrap-add]');
    if (giftWrapAddBtn) {
      e.stopPropagation();
      this._toggleGiftWrap(giftWrapAddBtn, true);
      return;
    }

    const giftWrapRemoveBtn = e.target.closest('[data-gift-wrap-remove]');
    if (giftWrapRemoveBtn) {
      e.stopPropagation();
      this._toggleGiftWrap(giftWrapRemoveBtn, false);
      return;
    }

    const suggestionBtn = e.target.closest('[data-add-to-cart-suggestion]');
    if (suggestionBtn) {
      e.stopPropagation();
      const variantId = suggestionBtn.dataset.variantId;
      if (variantId) this._addSuggestion(suggestionBtn, variantId);
      return;
    }

    const couponBtn = e.target.closest('[data-coupon-btn]');
    if (couponBtn) {
      e.stopPropagation();
      this._applyCoupon();
      return;
    }

    const removeCouponBtn = e.target.closest('[data-remove-coupon]');
    if (removeCouponBtn) {
      e.stopPropagation();
      this._removeCoupon();
    }
  }

  /** Lê a quantidade atual de um item pelo botão +/- */
  _getItemQty(btn) {
    const valueEl = btn.closest('[data-cart-item]')?.querySelector('[data-quantity-value]');
    return parseInt(valueEl?.textContent || '1');
  }

  _normalizeItemProperties(properties) {
    if (!properties) return {};

    if (Array.isArray(properties)) {
      return properties.reduce((normalized, entry) => {
        if (!entry) return normalized;

        if (Array.isArray(entry)) {
          const key = entry[0];
          const value = entry[1];

          if (key && value !== '' && value !== null && value !== undefined) {
            normalized[key] = value;
          }

          return normalized;
        }

        if (typeof entry === 'object') {
          const key = entry.first || entry[0];
          const value = entry.last || entry[1];

          if (key && value !== '' && value !== null && value !== undefined) {
            normalized[key] = value;
          }
        }

        return normalized;
      }, {});
    }

    if (typeof properties === 'object') {
      return { ...properties };
    }

    return {};
  }

  _getItemProperties(btn) {
    const raw = btn.closest('[data-cart-item]')?.querySelector('[data-line-item-properties]')?.textContent;
    if (!raw) return {};

    try {
      return this._normalizeItemProperties(JSON.parse(raw));
    } catch (error) {
      console.error('[CartDrawerManager] Failed to parse line item properties:', error);
      return {};
    }
  }

  async _removeItem(btn, key) {
    if (!window.CartManager || btn.disabled) return;
    btn.disabled = true;

    const result = await window.CartManager.removeFromCart(key);
    if (result.success) {
      await this.refresh();
      this._updateCartCount(result.data.item_count);
    }

    btn.disabled = false;
  }

  async _updateQty(btn, key, quantity) {
    if (!window.CartManager || btn.disabled) return;
    btn.disabled = true;

    const result = await window.CartManager.updateQuantity(key, quantity);
    if (result.success) {
      await this.refresh();
      this._updateCartCount(result.data.item_count);
    }

    btn.disabled = false;
  }

  async _toggleGiftWrap(btn, shouldAdd) {
    if (!window.CartManager || btn.disabled) return;

    const lineIndex = parseInt(btn.dataset.lineItemIndex || btn.closest('[data-cart-item]')?.dataset.lineItemIndex || '', 10);
    const propertyName = btn.dataset.propertyName;
    const privatePropertyName = btn.dataset.privatePropertyName || '_gift_wrapping_state';
    const quantity = this._getItemQty(btn);

    if (!lineIndex || !propertyName || !quantity) return;

    btn.disabled = true;

    const properties = this._getItemProperties(btn);

    delete properties[privatePropertyName];

    if (shouldAdd) {
      properties[propertyName] = 'Yes';
    } else {
      delete properties[propertyName];
    }

    Object.keys(properties).forEach((propKey) => {
      const value = properties[propKey];
      if (value === '' || value === null || value === undefined) {
        delete properties[propKey];
      }
    });

    if (!shouldAdd && Object.keys(properties).length === 0) {
      properties[privatePropertyName] = 'removed';
    }

    const result = await window.CartManager.updateLinePropertiesByIndex(lineIndex, quantity, properties);
    if (result.success) {
      await this.refresh();
      this._updateCartCount(result.data.item_count);
    }

    btn.disabled = false;
  }

  async _addSuggestion(btn, variantId) {
    if (!window.CartManager) return;

    const result = await window.CartManager.addToCart(variantId, {
      quantity: 1,
      button: btn,
    });

    if (result.success) {
      await this.refresh();
      this._updateCartCount();
    }
  }

  async _applyCoupon() {
    const couponInput = this.drawer.querySelector('[data-coupon-input]');
    const couponBtn = this.drawer.querySelector('[data-coupon-btn]');
    const errorEl = this.drawer.querySelector('[data-coupon-error]');
    const code = couponInput?.value?.trim();

    if (errorEl) {
      errorEl.textContent = '';
      errorEl.classList.add('hidden');
    }

    if (!code) return;

    if (couponBtn) couponBtn.textContent = 'Aplicando...';

    try {
      await fetch(`${this.config.cartUrl}/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: code }),
      });

      await this.refresh();

      const appliedLabels = Array.from(
        this.drawer.querySelectorAll('[data-remove-coupon]')
      ).map((el) =>
        el.getAttribute('aria-label')?.replace('Remover cupom ', '').trim().toLowerCase()
      );

      if (!appliedLabels.some((c) => c === code.toLowerCase())) {
        const newErrorEl = this.drawer.querySelector('[data-coupon-error]');
        if (newErrorEl) {
          newErrorEl.textContent = `Cupom "${code}" inválido ou não encontrado.`;
          newErrorEl.classList.remove('hidden');
        }
      } else {
        const newInput = this.drawer.querySelector('[data-coupon-input]');
        if (newInput) newInput.value = '';
      }
    } catch (error) {
      console.error('[CartDrawerManager] Coupon error:', error);
      const newErrorEl = this.drawer.querySelector('[data-coupon-error]');
      if (newErrorEl) {
        newErrorEl.textContent = 'Erro ao aplicar cupom. Tente novamente.';
        newErrorEl.classList.remove('hidden');
      }
    } finally {
      const newCouponBtn = this.drawer.querySelector('[data-coupon-btn]');
      if (newCouponBtn) newCouponBtn.textContent = 'Aplicar';
    }
  }

  async _removeCoupon() {
    try {
      const response = await fetch(`${this.config.cartUrl}/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discount: '' }),
      });

      if (response.ok) await this.refresh();
    } catch (error) {
      console.error('[CartDrawerManager] Remove coupon error:', error);
    }
  }

  /**
   * Escuta os eventos DOM emitidos pelo CartManager e reage a eles.
   */
  _bindCartEvents() {
    document.addEventListener('cart:added', async (e) => {
      this.activeView = 'cart';
      await this.refresh();
      this._updateCartCount(await this._resolveCount(e));
      this.open();
    });

    document.addEventListener('cart:updated', async (e) => {
      await this.refresh();
      this._updateCartCount(await this._resolveCount(e));
    });

    document.addEventListener('cart:removed', async (e) => {
      await this.refresh();
      this._updateCartCount(await this._resolveCount(e));
    });

    window.addEventListener('favorites:updated', () => {
      if (this.activeView === 'favorites' && this.isOpen) {
        this._renderFavorites();
      }
    });
  }

  /**
   * Resolve o item_count do carrinho a partir do evento ou via API fallback.
   */
  async _resolveCount(event) {
    if (event.detail?.cart?.item_count !== undefined) return event.detail.cart.item_count;

    if (window.CartManager) {
      const cartData = await window.CartManager.getCart();
      if (cartData.success) return cartData.data.item_count;
    }

    return undefined;
  }

  /** Atualiza todos os contadores de itens visíveis na página. */
  _updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      if (count !== undefined) {
        el.textContent = `[${count}]`;
        el.style.display = '';
      }
    });

    document.querySelectorAll('[data-cart-count-header]').forEach((el) => {
      if (count !== undefined) el.textContent = count > 0 ? `[${count}]` : '';
    });
  }

}

// ─── Inicialização ────────────────────────────────────────────────────────────

function _initCartDrawerManager() {
  window.CartDrawerManager = new CartDrawerManager(window.cartDrawerConfig || {});
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', _initCartDrawerManager);
} else {
  _initCartDrawerManager();
}
